import PartyRelationship from '@/api/party-relationship'

// Operator/pricing group codes look like 'JBG-PM…' / 'FEX-PM…'; real operator
// groups ('Dash Mart - All', 'Pay-Less Stores') don't. Pricing groups are
// legitimate STORE_GROUP parties but noise in an ordering picker.
const PRICING_GROUP_PATTERN = /^[A-Z]{2,4}-PM/

/**
 * Derive store-group presets from the stores the user can already order for.
 *
 * No user→group links exist in the party model (verified in prod) — but the
 * user's own stores carry STORE_GROUP_MEMBER edges to their groups, so the
 * presets are computed from membership of *their* stores only. Selecting a
 * preset can therefore never grant a store the user doesn't already have.
 *
 * @param {Array<{id: string}>} userStores stores the user can order for
 * @returns {Promise<Array<{id: string, name: string, storeIds: string[]}>>}
 *          groups sorted by member count (largest first), pricing groups last
 */
export async function deriveStoreGroups (userStores) {
  const storeIds = (userStores || []).map(s => s?.id).filter(Boolean)
  if (storeIds.length === 0) return []

  const payload = {
    from_party_ids: storeIds,
    // everything except STORE_GROUP — the inverse of the sources lookup
    excluded_upstream_types: ['WHOLESALER', 'REGION_GROUP', 'AD_GROUP', 'OWNER', 'DISTRIBUTION_CENTER']
  }
  const res = await PartyRelationship.getUpstreamRelationships(payload)
  const rels = (res?.data || []).filter(r =>
    storeIds.includes(r.from_party_id) && r.from_rel_type === 'STORE_GROUP_MEMBER'
  )

  const groups = new Map()
  for (const rel of rels) {
    const group = groups.get(rel.to_party_id) || {
      id: rel.to_party_id,
      name: rel.to_party_name || rel.to_party_id,
      storeIds: []
    }
    if (!group.storeIds.includes(rel.from_party_id)) {
      group.storeIds.push(rel.from_party_id)
    }
    groups.set(rel.to_party_id, group)
  }

  return [...groups.values()]
    .filter(g => g.storeIds.length > 1) // a one-store "group" is just the store
    .sort((a, b) => {
      const aPricing = PRICING_GROUP_PATTERN.test(a.name)
      const bPricing = PRICING_GROUP_PATTERN.test(b.name)
      if (aPricing !== bPricing) return aPricing ? 1 : -1
      return b.storeIds.length - a.storeIds.length || a.name.localeCompare(b.name)
    })
}
